import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authPrimaryCtaSx } from '@components/layout/AuthFormCard';

const VerifyParentalConsentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [playerIdCode, setPlayerIdCode] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing parental consent token.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/auth/verify-parental-consent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const result = await response.json();
        if (cancelled) return;
        if (result.success) {
          setStatus('success');
          setMessage(result.message || 'Parental consent confirmed.');
          setPlayerIdCode(result.data?.playerIdCode || '');
          toast.success('Parental consent confirmed. You can sign in now.');
        } else {
          setStatus('error');
          setMessage(result.message || 'Failed to verify parental consent');
          toast.error(result.message || 'Failed to verify parental consent');
        }
      } catch {
        if (cancelled) return;
        setStatus('error');
        setMessage('An error occurred while verifying parental consent.');
        toast.error('An error occurred. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthPageShell>
      <AuthFormCard roleLabel="Family" title="Parental consent" chrome backTo="/login">
        {isLoading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Confirming parental consent…</Typography>
          </Box>
        )}

        {!isLoading && status === 'success' && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
            <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
              {message}
            </Alert>
            {playerIdCode && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Child Player ID: <strong>{playerIdCode}</strong>. The child signs in with this ID, not the parent email.
              </Typography>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/login')}
              sx={authPrimaryCtaSx}
            >
              Sign in now
            </Button>
          </Box>
        )}

        {!isLoading && status === 'error' && (
          <Box sx={{ textAlign: 'center' }}>
            <ErrorIcon color="error" sx={{ fontSize: 56, mb: 1 }} />
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button component={RouterLink} to="/login" variant="outlined" fullWidth>
              Back to login
            </Button>
          </Box>
        )}
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default VerifyParentalConsentPage;
